import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import Amplify, { Auth, API, graphqlOperation } from 'aws-amplify';
import {
  createStripeSubscription
} from "../../shared/data-services/graphql";
import './WebAppStripeCard.css';

const CARD_OPTIONS = {
  iconStyle: 'solid',
  hidePostalCode: true,
  style: {
    base: {
      iconColor: '#c4f0ff',
      color: '#000',
      fontWeight: 500,
      fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#adadad',
      },
    },
    invalid: {
      iconColor: '#ffc7ee',
      color: '#000000',
    },
  },
};

const CardField = ({ onChange }) => (
  <div className="FormRow">
    <CardElement options={CARD_OPTIONS} onChange={onChange} />
  </div>
);

const Field = ({
  label,
  id,
  type,
  placeholder,
  required,
  autoComplete,
  value,
  onChange,
  readonly
}) => (
    <div className="FormRow">
      <label htmlFor={id} className="FormRowLabel">
        {label}
      </label>
      { type == 'select' ?
        <select
          id={id}
          value={value}
          onChange={onChange}
        >
          <option value="DE">Deutschland</option>
{/*           <option value="IT">Italien</option>
 */}        </select>
        :
        <input
          className="FormRowInput"
          id={id}
          type={type}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          readonly={readonly}
        />
      }
    </div>
  );

const SubmitButton = ({ processing, error, children, disabled }) => (
  <button
    className={`SubmitButton ${error ? 'SubmitButton--error' : ''}`}
    type="submit"
    disabled={processing || disabled}
  >
    {processing ? 'Processing...' : children}
  </button>
);

const ErrorMessage = ({ children }) => (
  <div className="ErrorMessage" role="alert">
    <svg width="16" height="16" viewBox="0 0 17 17">
      <path
        fill="#FFF"
        d="M8.5,17 C3.80557963,17 0,13.1944204 0,8.5 C0,3.80557963 3.80557963,0 8.5,0 C13.1944204,0 17,3.80557963 17,8.5 C17,13.1944204 13.1944204,17 8.5,17 Z"
      />
      <path
        fill="#6772e5"
        d="M8.5,7.29791847 L6.12604076,4.92395924 C5.79409512,4.59201359 5.25590488,4.59201359 4.92395924,4.92395924 C4.59201359,5.25590488 4.59201359,5.79409512 4.92395924,6.12604076 L7.29791847,8.5 L4.92395924,10.8739592 C4.59201359,11.2059049 4.59201359,11.7440951 4.92395924,12.0760408 C5.25590488,12.4079864 5.79409512,12.4079864 6.12604076,12.0760408 L8.5,9.70208153 L10.8739592,12.0760408 C11.2059049,12.4079864 11.7440951,12.4079864 12.0760408,12.0760408 C12.4079864,11.7440951 12.4079864,11.2059049 12.0760408,10.8739592 L9.70208153,8.5 L12.0760408,6.12604076 C12.4079864,5.79409512 12.4079864,5.25590488 12.0760408,4.92395924 C11.7440951,4.59201359 11.2059049,4.59201359 10.8739592,4.92395924 L8.5,7.29791847 L8.5,7.29791847 Z"
      />
    </svg>
    {children}
  </div>
);

const ResetButton = ({ onClick }) => (
  <button type="button" className="ResetButton" onClick={onClick}>
    <svg width="32px" height="32px" viewBox="0 0 32 32">
      <path
        fill="#FFF"
        d="M15,7.05492878 C10.5000495,7.55237307 7,11.3674463 7,16 C7,20.9705627 11.0294373,25 16,25 C20.9705627,25 25,20.9705627 25,16 C25,15.3627484 24.4834055,14.8461538 23.8461538,14.8461538 C23.2089022,14.8461538 22.6923077,15.3627484 22.6923077,16 C22.6923077,19.6960595 19.6960595,22.6923077 16,22.6923077 C12.3039405,22.6923077 9.30769231,19.6960595 9.30769231,16 C9.30769231,12.3039405 12.3039405,9.30769231 16,9.30769231 L16,12.0841673 C16,12.1800431 16.0275652,12.2738974 16.0794108,12.354546 C16.2287368,12.5868311 16.5380938,12.6540826 16.7703788,12.5047565 L22.3457501,8.92058924 L22.3457501,8.92058924 C22.4060014,8.88185624 22.4572275,8.83063012 22.4959605,8.7703788 C22.6452866,8.53809377 22.5780351,8.22873685 22.3457501,8.07941076 L22.3457501,8.07941076 L16.7703788,4.49524351 C16.6897301,4.44339794 16.5958758,4.41583275 16.5,4.41583275 C16.2238576,4.41583275 16,4.63969037 16,4.91583275 L16,7 L15,7 L15,7.05492878 Z M16,32 C7.163444,32 0,24.836556 0,16 C0,7.163444 7.163444,0 16,0 C24.836556,0 32,7.163444 32,16 C32,24.836556 24.836556,32 16,32 Z"
      />
    </svg>
  </button>
);

const CheckoutForm = (input) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    email: JSON.parse(input.props.profile.appendix).email,
    name: '',
    address: {
      line1: '',
      postal_code: '',
      country: 'DE'
    }
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    if (error) {
      elements.getElement('card').focus();
      return;
    }

    if (cardComplete) {
      setProcessing(true);
    }

    var payload = null;
    var selectedPaymentMethod;
    if (!input.props.payment) {
      payload = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: billingDetails,
      });
      selectedPaymentMethod = payload.paymentMethod;
    } else {
      selectedPaymentMethod = input.props.payment;
      setProcessing(true);
    }

    console.log(selectedPaymentMethod);

    if (payload && payload.error) {
      setError(payload.error);
    } else {
      setPaymentMethod(paymentMethod);
      try {
        const result = await API.graphql(graphqlOperation(createStripeSubscription,
          {
            paymentMethodId: selectedPaymentMethod.id,
            street: billingDetails.address.line1,
            postal_code: billingDetails.address.postal_code,
            city: billingDetails.address.city,
            country: billingDetails.address.country,
          }));
        const subscription = JSON.parse(result.data.createStripeSubscription);
        console.log(subscription)
        if (subscription.object == 'subscription') {
          setSubscription(subscription);
          input.props.onSubscription();
        } else {
          setError({ message: 'Bezahlung Fehlgeschlagen' })
        }
      } catch (e) {
        setError({ message: 'Bezahlung Fehlgeschlagen' })
        console.log(e);
      }
      setProcessing(false);
    }
  };

  const reset = () => {
    setError(null);
    setProcessing(false);
    setPaymentMethod(null);
    setBillingDetails({
      email: '',
      phone: '',
      name: '',
    });
  };

  return subscription ? (
    <div className="Result">
      <div className="ResultTitle" role="alert">
        Erfolgreich abonniert!
      </div>
      <div className="ResultMessage">

      </div>
      <ResetButton onClick={reset} />
    </div>
  ) : (
      <form className="Form" onSubmit={handleSubmit}>
        <fieldset className="FormGroup">
          <Field
            label="Land"
            id="address-country"
            type="select"
            placeholder="Land"
            required
            readonly="readonly"
            autoComplete="country"
            value={billingDetails.address.country}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, address: { ...billingDetails.address, country: e.target.value } });
            }}
          />
        </fieldset>
        <fieldset className="FormGroup">
          <Field
            label="E-Mail"
            id="email"
            type="email"
            placeholder="E-Mail-Adresse"
            required
            readonly="readonly"
            autoComplete="email"
            value={billingDetails.email}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, email: e.target.value });
            }}
          />
          <Field
            label="Name"
            id="name"
            type="text"
            placeholder="Name des Karteninhabers"
            required
            autoComplete="name"
            value={billingDetails.name}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, name: e.target.value });
            }}
          />
        </fieldset>
        <fieldset className="FormGroup">
          <Field
            label="Adresse"
            id="address-line1"
            type="tel"
            placeholder="Straße und Hausnummer"
            required
            autoComplete="street-address"
            value={billingDetails.address.line1}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, address: { ...billingDetails.address, line1: e.target.value } });
            }}
          />
          <Field
            label="PLZ"
            id="address-postal-code"
            type="number"
            placeholder="Postleitzahl"
            required
            autoComplete="postal-code"
            value={billingDetails.address.postal_code}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, address: { ...billingDetails.address, postal_code: e.target.value } });
            }}
          />
          <Field
            label="Ort"
            id="address-city"
            type="text"
            placeholder="Ort"
            required
            autoComplete="address-level2"
            value={billingDetails.address.state}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, address: { ...billingDetails.address, city: e.target.value } });
            }}
          />
        </fieldset>
        <fieldset className="FormGroup">
          {!input.props.payment ?
            <CardField
              onChange={(e) => {
                setError(e.error);
                setCardComplete(e.complete);
              }}
            />
            :
            <Field
              label={input.props.payment.card.brand.toUpperCase()}
              id="card"
              type="text"
              readonly="readonly"
              value={
                ' •••• ' +
                input.props.payment.card.last4 +
                ' gültig bis ' + input.props.payment.card.exp_month +
                '/' + input.props.payment.card.exp_year
              }
            />
          }
        </fieldset>
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
        <SubmitButton processing={processing} error={error} disabled={!stripe}>
          Abonnement abschließen
      </SubmitButton>
      </form>
    );
};

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css?family=Roboto',
    },
  ],
};

const App = (props) => {

  // Make sure to call `loadStripe` outside of a component’s render to avoid
  // recreating the `Stripe` object on every render.
  const stripePromise = loadStripe(props.pk);

  return (

    <div className="AppWrapper">
      <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
        <CheckoutForm props={props} />
      </Elements>
    </div>
  );
};

export default App;